import React from 'react'
import type { Content, ContentStatus } from './types'
import { STATUS_PALETTE, STATUS_CONFIG } from './types'
import { useAppLanguage } from '../../i18n'

type ContentCardProps = {
  content: Content
  isWorking: boolean
  workingAction?: string
  onViewDetail: () => void
  onReindex: () => void
  onLLMSegment: () => void
  onForceTranscribe: () => void
  onDelete: () => void
}

/**
 * Carte de contenu avec statut animé et actions
 * Responsabilité unique : affichage d'un contenu
 */
export default function ContentCard({
  content,
  isWorking,
  workingAction,
  onViewDetail,
  onReindex,
  onLLMSegment,
  onForceTranscribe,
  onDelete,
}: ContentCardProps) {
  const { t, language } = useAppLanguage()
  const statusConfig = STATUS_CONFIG[content.status] || STATUS_CONFIG.uploaded
  const statusClass = STATUS_PALETTE[content.status] || 'bg-zinc-100 text-zinc-600'
  const isProcessing = content.status === 'transcribing' || content.status === 'indexing'
  const canForceTranscribe = content.status === 'uploaded' || content.status === 'error'

  const locale = language === 'en' ? 'en-US' : language === 'es' ? 'es-ES' : 'fr-FR'

  // Déterminer l'icône du type de contenu basé sur le titre ou autres indices
  const getContentIcon = () => {
    const title = content.title.toLowerCase()
    if (title.includes('video') || title.includes('vidéo') || title.match(/\.(mp4|mov|avi|mkv)$/i)) {
      return '🎥'
    }
    if (title.match(/\.(mp3|wav|ogg|m4a|flac)$/i) || title.includes('audio') || title.includes('podcast')) {
      return '🎵'
    }
    if (title.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) || title.includes('image')) {
      return '🖼️'
    }
    return '📄'
  }

  const isLLMSegmented = content.chunking_strategy === 'llm_segmentation' || content.chunking_strategy === 'json_segments'

  return (
    <div className="group rounded-2xl border border-zinc-200/70 bg-white/80 p-3 transition hover:border-emerald-200 hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900/80 dark:hover:border-emerald-800">
      {/* Ligne 1 : icône + titre + badge statut */}
      <div className="flex items-center gap-3 min-w-0">
        {/* Icône type */}
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-base ${
          isProcessing
            ? 'bg-blue-100 dark:bg-blue-900/40'
            : content.status === 'error'
              ? 'bg-red-100 dark:bg-red-900/40'
              : 'bg-emerald-100 dark:bg-emerald-900/40'
        }`}>
          {isProcessing ? (
            <svg className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <span className="text-sm">{getContentIcon()}</span>
          )}
        </div>

        {/* Titre */}
        <div className="flex-1 min-w-0">
          <h3 className="truncate text-sm font-semibold text-zinc-800 dark:text-zinc-100">{content.title}</h3>
          <p className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-0.5">
            ID {content.id}
            {content.last_indexed_at && (
              <span> · {t.knowledges.contentCard.indexedAtPrefix} {formatDate(content.last_indexed_at, locale, t)}</span>
            )}
          </p>
        </div>

        {/* Badge statut */}
        <span className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${statusClass}`}>
          {isProcessing && (
            <svg className="h-2.5 w-2.5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          )}
          {statusConfig.label}
        </span>
      </div>

      {/* Ligne 2 : actions */}
      <div className={`mt-2 flex items-center gap-1.5 pl-12 ${isWorking ? 'opacity-50 pointer-events-none' : ''}`}>
        {/* Détails */}
        <button type="button" onClick={onViewDetail} disabled={isWorking}
          className="rounded-lg border border-zinc-200/70 p-1.5 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-700 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
          title={t.knowledges.contentCard.viewDetails}>
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </button>

        {/* Ré-indexer */}
        <button type="button" onClick={onReindex} disabled={isWorking}
          className="rounded-lg border border-zinc-200/70 p-1.5 text-zinc-500 transition hover:bg-emerald-50 hover:text-emerald-600 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-emerald-900/40 dark:hover:text-emerald-400"
          title={t.knowledges.contentCard.reindexClassic}>
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>

        {/* Segmentation IA */}
        <button type="button" onClick={onLLMSegment} disabled={isWorking || isLLMSegmented}
          className={`rounded-lg border p-1.5 transition ${
            isLLMSegmented
              ? 'border-zinc-200/50 text-zinc-300 cursor-default dark:border-zinc-700/50 dark:text-zinc-600'
              : 'border-amber-300/70 text-amber-500 hover:bg-amber-50 hover:text-amber-600 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-900/40 dark:hover:text-amber-300'
          }`}
          title={isLLMSegmented ? t.knowledges.contentCard.alreadyLLMSegmented : t.knowledges.contentCard.llmSegmentation}>
          {workingAction === 'llm_seg' ? (
            <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          )}
        </button>

        {/* Forcer transcription */}
        {canForceTranscribe && (
          <button type="button" onClick={onForceTranscribe} disabled={isWorking}
            className="rounded-lg border border-zinc-200/70 p-1.5 text-zinc-500 transition hover:bg-blue-50 hover:text-blue-600 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-blue-900/40 dark:hover:text-blue-400"
            title={t.knowledges.contentCard.forceTranscribe}>
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
          </button>
        )}

        {/* Supprimer — poussé à droite */}
        <button type="button" onClick={onDelete} disabled={isWorking}
          className="ml-auto rounded-lg border border-zinc-200/70 p-1.5 text-zinc-500 transition hover:bg-red-50 hover:text-red-600 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-red-900/40 dark:hover:text-red-400"
          title={t.common.delete}>
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  )
}

function formatDate(
  value: string,
  locale: string,
  t: { knowledges: { contentCard: { yesterday: string; justNow: string; minutesAgo: string; hoursAgo: string; daysAgo: string } } }
) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)

  const interpolate = (template: string, n: number) => template.replace('{n}', String(n))
  
  if (diffMins < 1) return t.knowledges.contentCard.justNow
  if (diffMins < 60) return interpolate(t.knowledges.contentCard.minutesAgo, diffMins)
  if (diffHours < 24) return interpolate(t.knowledges.contentCard.hoursAgo, diffHours)
  if (diffDays === 1) return t.knowledges.contentCard.yesterday
  if (diffDays < 7) return interpolate(t.knowledges.contentCard.daysAgo, diffDays)
  return date.toLocaleDateString(locale, { day: '2-digit', month: 'short' })
}
